import {
  Viewer,
  Cartesian3,
  Matrix4,
  Entity,
  Ellipsoid,
  ConstantPositionProperty,
  Math,
  JulianDate,
  HorizontalOrigin,
  VerticalOrigin,
} from "cesium";
import proj4 from "proj4";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { ActivatableToolInterface } from "./ActivatableToolInterface";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";
import {
  SerializableToolInterface,
  compareToolState,
} from "./SerializableToolInterface";
import EventEmitter, { EventEmitterInterface } from "./EventEmitter";
import PositionRayPickerPlugin from "./PositionRayPickerPlugin";
import { EngineeringCsModel } from "components/modules/Projects/lib/types/EngineeringCsModel";
import { getEngineeringCsTransform } from "../utils/getEngineeringCsTransform";
import { HeightDatumCache } from "../utils/HeightDatumCache";
import { brisbaneTerrainCrs } from "../utils/brisbaneTerrainCrs";
import {
  ObservableProperty,
  DerivedObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";

export enum CoordinateMode {
  none,
  geographic,
  projected,
  engineering,
}

export const defaultCoordinateMode = CoordinateMode.projected;

export type CoordinatesToolSnapshot = {
  version: 1;
  coordinateMode: CoordinateMode;
  position: { x: number; y: number; z: number };
};

export function isCoordinatesStateApplied(
  state: CoordinatesToolSnapshot | undefined,
): boolean {
  return !!state?.position;
}

export default class CoordinatesToolPlugin
  implements
    AttachablePluginInterface,
    ActivatableToolInterface,
    SerializableToolInterface<CoordinatesToolSnapshot>,
    BackgroundableToolInterface
{
  private _viewer: Viewer = null;
  private _isActive = false;
  private _entityCursor: Entity = null;
  private _heightDatumCache = new HeightDatumCache();
  private _changeEvent = new EventEmitter();
  private _loadingEvent = new EventEmitter<boolean>();
  private _detachClickHandlerFunc: () => void;
  private _detachMoveHandlerFunc: () => void;

  private readonly _entityPin: ObservableProperty<Entity> =
    new ObservableProperty(null);
  private readonly _coordinateMode: ObservableProperty<CoordinateMode> =
    new ObservableProperty(defaultCoordinateMode);
  readonly isApplied = new DerivedObservableProperty(
    () => isCoordinatesStateApplied(this.capture()),
    [this._entityPin],
  );

  get changeEvent(): EventEmitterInterface {
    return this._changeEvent;
  }

  get loadingEvent(): EventEmitterInterface<boolean> {
    return this._loadingEvent;
  }

  get entityPin(): ReadonlyObservableProperty<Entity> {
    return this._entityPin;
  }

  get coordinateMode(): ReadonlyObservableProperty<CoordinateMode> {
    return this._coordinateMode;
  }

  constructor(private _positionPicker: PositionRayPickerPlugin) {}

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
  }

  detachViewer() {
    this._viewer = null;
  }

  setCoordinateMode(mode: CoordinateMode) {
    this._coordinateMode.set(mode);
  }

  clear(): void {
    this.removeEntityCursorIfPresent();
    this.removeEntityPinIfPresent();
    this._viewer?.scene.requestRender();
    this._changeEvent.emit();
  }

  setActive(value: boolean) {
    if (value) {
      this.loadTerrainDataIfRequired().then(() => {
        this._isActive = true;
        this.attachClickHandler(this._positionPicker.leftClickEvent);
        this.attachMoveHandler(this._positionPicker.moveEvent);
      });
    } else {
      this._isActive = false;
      this._detachClickHandlerFunc?.();
      this._detachMoveHandlerFunc?.();
      this.removeEntityCursorIfPresent();
      this._viewer?.scene.requestRender();
    }
    this._changeEvent.emit();
  }

  getValues(
    projectedCsWithVertical?: string,
    projectedCsProjText?: string,
    engineeringCs?: EngineeringCsModel,
  ) {
    const values: {
      geographic: { longitude: number; latitude: number; altitude?: number };
      projected: { eastings: number; northings: number; elevation?: number };
      engineering: { eastings: number; northings: number; elevation?: number };
    } = {
      geographic: null,
      projected: null,
      engineering: null,
    };

    const cartesian = this.getPinPosition() ?? this.getCursorPosition();
    const wgs84 = cartesian && this.cartesianToWgs84Degrees(cartesian);
    if (wgs84) {
      values.geographic = wgs84;
    }

    if (projectedCsProjText && values.geographic) {
      // We don't have to apply AxisOrder here because proj4 doesn't use it.
      // If the JavaScript port of proj gets updated to version 6 or above,
      // we will need to handle it here.
      const proj = proj4(projectedCsProjText);
      const en = proj.forward([wgs84.longitude, wgs84.latitude]);
      values.projected = {
        eastings: en[0],
        northings: en[1],
        elevation: null,
      };
      var heightAdjust = this._heightDatumCache.query(
        projectedCsWithVertical,
        en[0],
        en[1],
      );
      if (heightAdjust != null) {
        values.projected.elevation = wgs84.altitude + heightAdjust;
      }
    }

    if (engineeringCs?.transformation && values.projected) {
      const engMatrix = getEngineeringCsTransform(engineeringCs);
      const elevationPresent = values.projected.elevation != null;
      const projectCartesian = new Cartesian3(
        values.projected.eastings,
        values.projected.northings,
        values.projected.elevation,
      );
      const engCsLocation = Matrix4.multiplyByPoint(
        Matrix4.inverse(engMatrix, new Matrix4()),
        projectCartesian,
        new Cartesian3(),
      );
      values.engineering = {
        eastings: engCsLocation.x,
        northings: engCsLocation.y,
        elevation: elevationPresent ? engCsLocation.z : null,
      };
    }
    return values;
  }

  private getPinPosition() {
    return (
      this._entityPin.getSnapshot()?.position?.getValue(JulianDate.now()) ??
      null
    );
  }

  private getCursorPosition() {
    return this._entityCursor?.position?.getValue(JulianDate.now()) ?? null;
  }

  private attachClickHandler(
    clickEvent: typeof this._positionPicker.leftClickEvent,
  ) {
    this._detachClickHandlerFunc?.();
    this._detachClickHandlerFunc = clickEvent.addHandler((ev) => {
      if (!this._viewer || !this._isActive) return;
      if (!ev) {
        this.removeEntityPinIfPresent();
        this.removeEntityCursorIfPresent();
      } else {
        if (!this._entityPin.getSnapshot()) {
          this.removeEntityCursorIfPresent();
          this.addEntityPin(ev.position);
        } else {
          this.removeEntityPinIfPresent();
          if (!this._entityCursor) this.addEntityCursor(ev.position);
        }
      }
      this._viewer.scene.requestRender();
      this._changeEvent.emit();
    });
  }

  private attachMoveHandler(moveEvent: typeof this._positionPicker.moveEvent) {
    this._detachMoveHandlerFunc?.();
    this._detachMoveHandlerFunc = moveEvent.addHandler((ev) => {
      if (!this._viewer || !this._isActive || !!this._entityPin.getSnapshot())
        return;
      if (!ev) {
        this.removeEntityCursorIfPresent();
      } else {
        if (!this._entityCursor) {
          this.addEntityCursor(ev.position);
        } else {
          this._entityCursor.position = new ConstantPositionProperty(
            ev.position,
          );
        }
      }
      this._viewer.scene.requestRender();
      this._changeEvent.emit();
    });
  }

  private addEntityPin(position: Cartesian3) {
    return this._entityPin.set(
      this._viewer.entities.add({
        position,
        billboard: {
          image: "/images/billboards/coordinates-pinned.svg",
          scale: 0.2,
          verticalOrigin: VerticalOrigin.BOTTOM,
          horizontalOrigin: HorizontalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        properties: {
          excludeFromPicking: true,
        },
      }),
    );
  }

  private addEntityCursor(position: Cartesian3) {
    return (this._entityCursor = this._viewer.entities.add({
      position,
      billboard: {
        image: "/images/billboards/coordinates-cursor.svg",
        scale: 0.15,
        verticalOrigin: VerticalOrigin.BOTTOM,
        horizontalOrigin: HorizontalOrigin.CENTER,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      properties: {
        excludeFromPicking: true,
      },
    }));
  }

  private removeEntityCursorIfPresent() {
    if (this._entityCursor) this._viewer?.entities.remove(this._entityCursor);
    this._entityCursor = null;
  }

  private removeEntityPinIfPresent() {
    if (this._entityPin.getSnapshot())
      this._viewer?.entities.remove(this._entityPin.getSnapshot());
    this._entityPin.set(null);
  }

  private cartesianToWgs84Degrees(position: Cartesian3): {
    longitude: number;
    latitude: number;
    altitude: number;
  } {
    const cartographic = Ellipsoid.WGS84.cartesianToCartographic(position);
    if (!cartographic) return null;
    return {
      longitude: cartographic.longitude * Math.DEGREES_PER_RADIAN,
      latitude: cartographic.latitude * Math.DEGREES_PER_RADIAN,
      altitude: cartographic.height,
    };
  }

  /**
   * XXX Seed the height datum cache with Brisbane.
   */
  private async loadTerrainDataIfRequired() {
    if (!this._heightDatumCache.hasInsertedPoints(brisbaneTerrainCrs)) {
      this._loadingEvent.emit(true);
      await fetch("/brisbane-terrain-points.json")
        .then((res) => res.json())
        .then((data) => {
          this._heightDatumCache.insertPoints(brisbaneTerrainCrs, data);
        })
        .catch(() => {
          console.error("Failed to load terrain points for coordinates tool.");
        })
        .finally(() => {
          this._loadingEvent.emit(false);
        });
    }
  }

  isActive(): boolean {
    return this._isActive;
  }

  activate(): void {
    this.setActive(true);
  }

  deactivate(): void {
    this.setActive(false);
  }

  capture(): CoordinatesToolSnapshot | undefined {
    const cartesian = this.getPinPosition();
    if (!cartesian) return undefined;
    return {
      version: 1,
      coordinateMode: this._coordinateMode.getSnapshot(),
      position: { x: cartesian.x, y: cartesian.y, z: cartesian.z },
    };
  }

  restore(state: CoordinatesToolSnapshot | undefined): void {
    if (!state?.position || !state.coordinateMode) return;
    const { x, y, z } = state.position;
    if (x == null || y == null || z == null) return;

    this.setCoordinateMode(state.coordinateMode);
    this.removeEntityCursorIfPresent();
    this.addEntityPin(new Cartesian3(x, y, z));
    this._viewer.scene.requestRender();
    this._changeEvent.emit();
  }

  isStateApplied(state: CoordinatesToolSnapshot | undefined): boolean {
    return isCoordinatesStateApplied(state);
  }

  isMatchingState(state: CoordinatesToolSnapshot | undefined): boolean {
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }
}
